import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Users, FileStack, Calendar, ArrowUpRight, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PLAN_COLORS = {
  starter: "bg-zinc-50 border-zinc-200 text-zinc-700",
  pro: "bg-blue-50 border-blue-200 text-blue-700",
  enterprise: "bg-violet-50 border-violet-200 text-violet-700",
};
const STATUS_COLORS = {
  trial: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  past_due: "bg-red-50 text-red-700 border-red-200",
  canceled: "bg-zinc-100 text-zinc-700 border-zinc-200",
};
const STATUS_LABEL = {
  trial: "Gratis trial",
  active: "Aktiv",
  past_due: "Förfallen betalning",
  canceled: "Uppsagd",
};

export default function BillingManage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/orgs/me")
      .then((r) => setOrg(r.data))
      .catch(() => toast.error("Kunde inte hämta organisation"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!window.confirm("Vill du verkligen säga upp prenumerationen? Organisationen blir kvar tills perioden tar slut.")) return;
    setCanceling(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Prenumerationen har sagts upp");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte säga upp");
    } finally {
      setCanceling(false);
    }
  };

  if (loading || !org) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  }

  const seatsBar = org.seats_limit ? (org.seats_used / org.seats_limit) * 100 : 12;
  const casesBar = org.cases_limit_per_month ? (org.cases_this_month / org.cases_limit_per_month) * 100 : 12;
  const seatsCritical = org.seats_limit && org.seats_used >= org.seats_limit;
  const casesCritical = org.cases_limit_per_month && org.cases_this_month >= org.cases_limit_per_month;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">Prenumeration & fakturering</div>
      <h1 className="text-3xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        {org.name}
      </h1>
      <p className="mt-1 text-sm text-zinc-600">Organisation · {org.slug}</p>

      <div className="mt-7 bg-white border border-zinc-200 rounded-sm p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Aktiv plan</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1.5 text-sm font-semibold uppercase rounded-sm border ${PLAN_COLORS[org.plan] || "bg-zinc-50"}`}>
                {org.plan}
              </span>
              <span className={`px-2.5 py-1 text-xs font-medium border rounded-sm ${STATUS_COLORS[org.status]}`}>
                {STATUS_LABEL[org.status]}
              </span>
            </div>
            {org.trial_ends_at && org.status === "trial" && (
              <p className="mt-3 text-xs text-amber-700 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Trial avslutas {new Date(org.trial_ends_at).toLocaleDateString("sv-SE")}
              </p>
            )}
          </div>
          <button onClick={() => nav("/billing/plans")} data-testid="change-plan-btn"
            className="px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm inline-flex items-center gap-2">
            {org.plan === "starter" ? "Uppgradera" : "Byt plan"} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-7 grid sm:grid-cols-2 gap-5">
          <div data-testid="usage-seats">
            <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5">
              <span className="flex items-center gap-1.5 font-semibold"><Users className="w-3.5 h-3.5" /> Handläggarplatser</span>
              <span>{org.seats_used} / {org.seats_limit ?? "∞"}</span>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
              <div className={`h-full transition-all ${seatsCritical ? "bg-red-600" : "bg-blue-700"}`}
                   style={{ width: `${Math.min(seatsBar, 100)}%` }} />
            </div>
            {seatsCritical && (
              <p className="mt-1.5 text-[11px] text-red-700 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Platsgräns nådd – uppgradera för fler handläggare
              </p>
            )}
          </div>
          <div data-testid="usage-cases">
            <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5">
              <span className="flex items-center gap-1.5 font-semibold"><FileStack className="w-3.5 h-3.5" /> Ärenden denna månad</span>
              <span>{org.cases_this_month} / {org.cases_limit_per_month ?? "∞"}</span>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
              <div className={`h-full transition-all ${casesCritical ? "bg-red-600" : "bg-blue-700"}`}
                   style={{ width: `${Math.min(casesBar, 100)}%` }} />
            </div>
            {casesCritical && (
              <p className="mt-1.5 text-[11px] text-red-700 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Månadens kvot är nådd
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-7">
        <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Fakturering</h2>
        <p className="mt-1.5 text-sm text-zinc-600">Hanterad via Stripe (testläge). Vid byte av plan dirigeras du till säker betalsida.</p>
        <div className="mt-4 flex items-center gap-3 text-sm text-zinc-700">
          <CreditCard className="w-4 h-4 text-zinc-500" />
          <span>Stripe kund-ID: <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{org.stripe_customer_id || "(skapas vid första betalning)"}</code></span>
        </div>

        {user?.user_id === org.owner_user_id && org.status === "active" && (
          <button onClick={cancel} disabled={canceling} data-testid="cancel-subscription"
            className="mt-6 px-3 py-2 text-xs font-medium text-red-700 border border-red-200 hover:bg-red-50 rounded-sm">
            {canceling ? "Säger upp..." : "Säg upp prenumeration"}
          </button>
        )}
        {org.status === "canceled" && (
          <div className="mt-5 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm text-zinc-700 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-zinc-500 mt-0.5" />
            Prenumerationen är uppsagd. Klicka på "Byt plan" för att aktivera igen.
          </div>
        )}
      </div>
    </div>
  );
}
